/* eslint-disable operator-linebreak */
import FormatFloat from 'common/components/FormatPercent/FormatFloat';
import Image from 'common/components/Image';
import { Treasury } from 'common/models/treasury';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import React from 'react';
import { CryptoUtils } from 'utils/cryptoUtils';
import { ExternalLinkButton } from '../LinkButton/ExternalLinkButton';
import { LinkButton } from '../LinkButton/LinkButton';
import { OptionalOutboundLink } from '../OptionalOutboundLink';
import { TokenIcon, TokenName } from '../TokenIcons/TokenIcon';
import { OutboundLinkWrapper } from './treasuryTable.style';
import AddToWallet from 'common/components/AddToWallet';

type Props = {
  position: Treasury;
};

const getCollateralUSD = (position: Treasury): number => {
  if (!position) {
    return 0;
  }

  return position.collateralUSD - position.collateralPositionsUSD;
};

const getStimulusUSD = (position: Treasury): number => {
  if (!position) {
    return 0;
  }

  return position.stimulusUSD - position.stimulusPositionsUSD;
};

const TreasuryTablePosition: React.FC<Props> = (props) => {
  const { link: etherscanLink } = CryptoUtils.getScannerInfo(
    props.position.chainId,
    props.position.address,
    'token'
  ) as { link: string };
  const { link: strategyLink } = CryptoUtils.getScannerInfo(
    props.position.chainId,
    props.position.strategy,
    'address'
  ) as { link: string };

  return (
    <tr
      className="table-border-bottom primary-text-color"
      key={props.position.name}
    >
      {/* ---------------------- */}
      {/* Coin */}
      {/* ---------------------- */}
      <td>
        <div className="flex flex-row justify-start items-center">
          <TokenIcon
            name={props.position.name as TokenName}
            width="43px"
            marginRight="8px"
          />
          <AddToWallet treasury={props.position} />
        </div>
        <OptionalOutboundLink href={etherscanLink}>
          <div className="coin-name">
            {props.position.displayName}
            {etherscanLink && (
              <Image
                className="icon-new-window"
                alt="new window icon"
                marginLeft="5px"
              />
            )}
          </div>
        </OptionalOutboundLink>
      </td>
      {/* ---------------------- */}
      {/* Minted */}
      {/* ---------------------- */}
      <td>
        <FormatFloat value={props.position.circulation} prefix="$" />
        <div className="sub-text">
          <FormatFloat
            value={props.position.circulation}
            postfix={` ${props.position.displayName}`}
          />
        </div>
      </td>
      {/* ---------------------- */}
      {/* Collateral */}
      {/* ---------------------- */}
      <td>
        <div className="flex flex-col">
          <FormatFloat value={getCollateralUSD(props.position)} prefix="$" />
          {props.position.collateral?.map((collateral) => (
            <FormatFloat
              value={collateral.balance}
              postfix={` ${collateral.name}`}
              className="sub-text"
              key={collateral.name}
            />
          ))}
        </div>
      </td>
      {/* ---------------------- */}
      {/* Treasury */}
      {/* ---------------------- */}
      <td>
        <div className="flex flex-col">
          <FormatFloat value={getStimulusUSD(props.position)} prefix="$" />
          {props.position.stimulus?.map((stimulus) => (
            <FormatFloat
              value={stimulus.balance}
              postfix={` ${stimulus.name}`}
              trunc={false}
              className="sub-text"
              key={stimulus.name}
            />
          ))}
        </div>
      </td>
      {/* ---------------------- */}
      {/* Collateral Positions */}
      {/* ---------------------- */}
      <td>
        <div className="flex flex-col">
          <FormatFloat
            value={props.position.collateralPositionsUSD}
            prefix="$"
          />
          {props.position.collateralPositions?.map((item) => (
            <div key={item.name} className="flex flex-col sub-text">
              {item.assets?.map((asset) => (
                <FormatFloat
                  value={asset.balance}
                  postfix={` ${asset.name}`}
                  key={asset.name}
                />
              ))}
            </div>
          ))}
        </div>
      </td>
      {/* ---------------------- */}
      {/* Treasury Positions     */}
      {/* ---------------------- */}
      <td>
        <div className="flex flex-row">
          <FormatFloat value={props.position.stimulusPositionsUSD} prefix="$" />
          {strategyLink && (
            <OutboundLinkWrapper>
              <OutboundLink target="_blank" href={strategyLink}>
                <Image
                  className="icon-new-window"
                  alt="new window icon"
                  marginLeft="5px"
                />
              </OutboundLink>
            </OutboundLinkWrapper>
          )}
        </div>
        {props.position.stimulusPositions?.map((item) => (
          <div key={item.name} className="sub-text">
            {item.assets?.map((asset) => (
              <FormatFloat
                value={asset.balance}
                postfix={` ${asset.name}`}
                key={asset.name}
              />
            ))}
          </div>
        ))}
      </td>
      {/* ---------------------- */}
      {/* Reserve Ratio */}
      {/* ---------------------- */}
      <td>
        {props.position.reserveRatio < 100 ? (
          <FormatFloat value={props.position.reserveRatio * 100} postfix="%" />
        ) : (
          <>&infin;</>
        )}
      </td>
      {/* ---------------------- */}
      {/* Actions Column */}
      {/* ---------------------- */}
      <td className="w-44">
        {props.position.oneTokenVersion === 2 && (
          <LinkButton
            to={`/mint?name=${props.position.name}&collateral=${props.position.collateral[0].name}`}
            id={props.position.name}
            className="w-40"
          >
            Mint
          </LinkButton>
        )}
        {props.position.oneTokenVersion === 1 && (
          <ExternalLinkButton
            to={`https://www.ichi.farm/#/mint?${props.position.stimulus[0].name}`}
            className="w-40"
          >
            Mint
          </ExternalLinkButton>
        )}
      </td>
    </tr>
  );
};

export default TreasuryTablePosition;
